import React from 'react';
import { PiggyBank, Receipt } from 'lucide-react';
import { TaxCalculationResult } from '../../utils/taxUtils';
import { formatCurrency } from '../../lib/format';
import { Card, CardBody, CardHeader } from '../ui/card';
import { EmptyState } from '../ui/empty-state';

interface TaxSummaryCardProps {
  taxYear: string;
  result: TaxCalculationResult | null;
  /** Other income isn't PAYE, so its own tax isn't included in these figures. */
  otherIncomeNotIncluded?: boolean;
}

const LineItem: React.FC<{ label: React.ReactNode; value: string; swatch?: string; strong?: boolean; muted?: boolean }> = ({
  label,
  value,
  swatch,
  strong,
  muted,
}) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <span className={`flex items-center gap-2 text-[13px] ${strong ? 'font-medium text-fg' : 'text-fg-muted'}`}>
      {swatch && <span className={`size-2 shrink-0 rounded-full ${swatch}`} />}
      {label}
    </span>
    <span className={`numeric ${strong ? 'text-[15px] text-fg' : muted ? 'text-[13px] text-fg-muted' : 'text-[13px] text-fg'}`}>
      {value}
    </span>
  </div>
);

export const TaxSummaryCard: React.FC<TaxSummaryCardProps> = ({ taxYear, result, otherIncomeNotIncluded }) => {
  if (!result) {
    return (
      <Card>
        <CardHeader icon={<Receipt />} title="Summary" description={`Tax year ${taxYear}`} />
        <CardBody>
          <EmptyState
            icon={<Receipt />}
            title="Nothing to calculate yet"
            description="Enter your monthly earnings to see your estimated tax and take-home pay."
          />
        </CardBody>
      </Card>
    );
  }

  const { annualEarnings, expensesLogged, deduction, deductionType, profit, incomeTax, nationalInsurance, takeHomePay, taxSavedByDeduction } = result;
  const totalTax = incomeTax + nationalInsurance;
  const pct = (n: number) => (annualEarnings > 0 ? Math.max(0, (n / annualEarnings) * 100) : 0);
  const effectiveRate = profit > 0 ? (totalTax / profit) * 100 : 0;

  return (
    <Card className="relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -top-24 -right-20 size-64 rounded-full bg-success/10 blur-3xl" />
      <CardHeader icon={<Receipt />} title="Summary" description={`Tax year ${taxYear}`} className="relative" />
      <CardBody className="relative">
        <p className="text-xs font-medium text-fg-subtle">Estimated take-home</p>
        <p className="numeric mt-1 text-[34px] leading-none font-medium text-success">{formatCurrency(takeHomePay)}</p>
        <p className="numeric mt-1.5 text-[13px] text-fg-muted">
          {formatCurrency(takeHomePay / 12)} <span className="text-fg-subtle">per month, after expenses and tax</span>
        </p>

        {/* Where the earnings go */}
        <div
          className="mt-5 flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-white/[0.05]"
          role="img"
          aria-label={`Take-home ${pct(takeHomePay).toFixed(0)}%, expenses ${pct(expensesLogged).toFixed(0)}%, income tax ${pct(incomeTax).toFixed(0)}%, national insurance ${pct(nationalInsurance).toFixed(0)}%`}
        >
          <span className="bg-success" style={{ width: `${pct(takeHomePay)}%` }} />
          {expensesLogged > 0 && <span className="bg-info" style={{ width: `${pct(expensesLogged)}%` }} />}
          <span className="bg-danger" style={{ width: `${pct(incomeTax)}%` }} />
          <span className="bg-warning" style={{ width: `${pct(nationalInsurance)}%` }} />
        </div>
        <p className="mt-2 text-xs text-fg-subtle">
          Effective rate on profit <span className="numeric text-fg-muted">{effectiveRate.toFixed(1)}%</span>
        </p>

        <div className="mt-4 divide-y divide-line border-t border-line">
          <LineItem label="Earnings" value={formatCurrency(annualEarnings)} swatch="bg-success" />
          {deductionType === 'trading-allowance' ? (
            <LineItem
              label={
                <span>
                  Trading allowance
                  <span className="block text-[11px] text-fg-subtle">
                    Higher than your {formatCurrency(expensesLogged)} of expenses
                  </span>
                </span>
              }
              value={`−${formatCurrency(deduction)}`}
              swatch="bg-info"
              muted
            />
          ) : (
            <LineItem label="Expenses" value={`−${formatCurrency(deduction)}`} swatch="bg-info" muted />
          )}
          <LineItem label="Taxable profit" value={formatCurrency(profit)} strong />
          <LineItem label="Income Tax" value={formatCurrency(incomeTax)} swatch="bg-danger" />
          <LineItem label="Class 4 National Insurance" value={formatCurrency(nationalInsurance)} swatch="bg-warning" />
          <LineItem label="Total to pay HMRC" value={formatCurrency(totalTax)} strong />
        </div>

        {taxSavedByDeduction > 0 && (
          <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-info/20 bg-info/[0.07] px-3 py-2.5 text-[13px] text-[#b5d8ff]">
            <PiggyBank className="mt-px size-4 shrink-0" />
            <span>
              {deductionType === 'trading-allowance' ? 'The trading allowance' : 'Your expenses'} cut your bill by{' '}
              <span className="numeric font-medium text-fg">{formatCurrency(taxSavedByDeduction)}</span>.
            </span>
          </div>
        )}

        {otherIncomeNotIncluded && (
          <p className="mt-3 text-xs text-fg-subtle">
            Your other income is pushing this profit into higher bands, but tax on that income itself isn't included here.
          </p>
        )}
      </CardBody>
    </Card>
  );
};
